import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import { ArrowBack, Save, TrendingUp, TrendingDown, SwapHoriz } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function Transacciones() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  const [cajas, setCajas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [transacciones, setTransacciones] = useState([]);
  
  const [formData, setFormData] = useState({
    tipo: 'INGRESO',
    fecha: new Date().toISOString().split('T')[0],
    monto: '',
    cuenta_origen_id: '',
    tipo_cuenta_origen: '',
    cuenta_destino_id: '',
    tipo_cuenta_destino: '',
    concepto: '',
    referencia: ''
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [cajasRes, bancosRes, transRes] = await Promise.all([
        axios.get(`${API_URL}/api/tesoreria/cajas`, config),
        axios.get(`${API_URL}/api/tesoreria/bancos`, config),
        axios.get(`${API_URL}/api/tesoreria/transacciones`, config)
      ]);

      setCajas(cajasRes.data.data);
      setBancos(bancosRes.data.data);
      setTransacciones(transRes.data.data);
    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError('Error al cargar datos');
    }
  };

  const handleTipoChange = (tipo) => {
    setFormData({
      ...formData,
      tipo,
      cuenta_origen_id: '',
      tipo_cuenta_origen: '',
      cuenta_destino_id: '',
      tipo_cuenta_destino: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.post(`${API_URL}/api/tesoreria/transacciones`, formData, config);

      setSuccess('Transacción registrada exitosamente');
      setFormData({
        tipo: 'INGRESO',
        fecha: new Date().toISOString().split('T')[0],
        monto: '',
        cuenta_origen_id: '',
        tipo_cuenta_origen: '',
        cuenta_destino_id: '',
        tipo_cuenta_destino: '',
        concepto: '',
        referencia: ''
      });
      
      cargarDatos();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al registrar transacción:', err);
      setError(err.response?.data?.message || 'Error al registrar transacción');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'INGRESO': return <TrendingUp />;
      case 'EGRESO': return <TrendingDown />;
      case 'TRANSFERENCIA': return <SwapHoriz />;
      default: return null;
    }
  };

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'INGRESO': return 'success';
      case 'EGRESO': return 'error';
      case 'TRANSFERENCIA': return 'info';
      default: return 'default';
    }
  };

  const getCuentaNombre = (cuentaId, tipoCuenta) => {
    if (tipoCuenta === 'CAJA') {
      const caja = cajas.find(c => c.id === cuentaId);
      return caja ? `Caja: ${caja.nombre}` : 'Caja no encontrada';
    } else if (tipoCuenta === 'BANCO') {
      const banco = bancos.find(b => b.id === cuentaId);
      return banco ? `${banco.nombre_banco} - ${banco.numero_cuenta}` : 'Banco no encontrado';
    }
    return '-';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/tesoreria')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Transacciones
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Formulario */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Nueva Transacción
            </Typography>
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Tipo de Transacción</InputLabel>
                    <Select
                      value={formData.tipo}
                      onChange={(e) => handleTipoChange(e.target.value)}
                      label="Tipo de Transacción"
                    >
                      <MenuItem value="INGRESO">Ingreso</MenuItem>
                      <MenuItem value="EGRESO">Egreso</MenuItem>
                      <MenuItem value="TRANSFERENCIA">Transferencia</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="Fecha"
                    value={formData.fecha}
                    onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Monto"
                    value={formData.monto}
                    onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                    inputProps={{ step: 0.01, min: 0.01 }}
                  />
                </Grid>

                {/* Cuenta Origen (para EGRESO y TRANSFERENCIA) */}
                {(formData.tipo === 'EGRESO' || formData.tipo === 'TRANSFERENCIA') && (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Tipo Cuenta Origen</InputLabel>
                        <Select
                          value={formData.tipo_cuenta_origen}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            tipo_cuenta_origen: e.target.value,
                            cuenta_origen_id: ''
                          })}
                          label="Tipo Cuenta Origen"
                        >
                          <MenuItem value="CAJA">Caja</MenuItem>
                          <MenuItem value="BANCO">Banco</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {formData.tipo_cuenta_origen && (
                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <InputLabel>Cuenta Origen</InputLabel>
                          <Select
                            value={formData.cuenta_origen_id}
                            onChange={(e) => setFormData({ ...formData, cuenta_origen_id: e.target.value })}
                            label="Cuenta Origen"
                          >
                            {formData.tipo_cuenta_origen === 'CAJA' ? (
                              (cajas || []).filter(c => c.activa).map(caja => (
                                <MenuItem key={caja.id} value={caja.id}>
                                  {caja.nombre} - {formatCurrency(caja.saldo_actual)}
                                </MenuItem>
                              ))
                            ) : (
                              (bancos || []).filter(b => b.activa).map(banco => (
                                <MenuItem key={banco.id} value={banco.id}>
                                  {banco.nombre_banco} - {banco.numero_cuenta} - {formatCurrency(banco.saldo_actual)}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </>
                )}

                {/* Cuenta Destino (para INGRESO y TRANSFERENCIA) */}
                {(formData.tipo === 'INGRESO' || formData.tipo === 'TRANSFERENCIA') && (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Tipo Cuenta Destino</InputLabel>
                        <Select
                          value={formData.tipo_cuenta_destino}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            tipo_cuenta_destino: e.target.value,
                            cuenta_destino_id: ''
                          })}
                          label="Tipo Cuenta Destino"
                        >
                          <MenuItem value="CAJA">Caja</MenuItem>
                          <MenuItem value="BANCO">Banco</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {formData.tipo_cuenta_destino && (
                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <InputLabel>Cuenta Destino</InputLabel>
                          <Select
                            value={formData.cuenta_destino_id}
                            onChange={(e) => setFormData({ ...formData, cuenta_destino_id: e.target.value })}
                            label="Cuenta Destino"
                          >
                            {formData.tipo_cuenta_destino === 'CAJA' ? (
                              (cajas || []).filter(c => c.activa).map(caja => (
                                <MenuItem key={caja.id} value={caja.id}>
                                  {caja.nombre} - {formatCurrency(caja.saldo_actual)}
                                </MenuItem>
                              ))
                            ) : (
                              (bancos || []).filter(b => b.activa).map(banco => (
                                <MenuItem key={banco.id} value={banco.id}>
                                  {banco.nombre_banco} - {banco.numero_cuenta} - {formatCurrency(banco.saldo_actual)}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Concepto"
                    value={formData.concepto}
                    onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Referencia"
                    value={formData.referencia}
                    onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                    placeholder="Número de documento, comprobante, etc."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    disabled={loading}
                  >
                    {loading ? 'Guardando...' : 'Registrar Transacción'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {/* Listado de Transacciones */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Últimas Transacciones
            </Typography>
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Concepto</TableCell>
                    <TableCell align="right">Monto</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!transacciones || transacciones.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No hay transacciones registradas
                      </TableCell>
                    </TableRow>
                  ) : (
                    transacciones.slice(0, 10).map((trans) => (
                    <TableRow key={trans.id} hover>
                      <TableCell>
                        {new Date(trans.fecha).toLocaleDateString('es-EC')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getTipoIcon(trans.tipo)}
                          label={trans.tipo}
                          color={getTipoColor(trans.tipo)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{trans.concepto}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {trans.tipo === 'INGRESO' && getCuentaNombre(trans.cuenta_destino_id, trans.tipo_cuenta_destino)}
                          {trans.tipo === 'EGRESO' && getCuentaNombre(trans.cuenta_origen_id, trans.tipo_cuenta_origen)}
                          {trans.tipo === 'TRANSFERENCIA' && 
                            `${getCuentaNombre(trans.cuenta_origen_id, trans.tipo_cuenta_origen)} → ${getCuentaNombre(trans.cuenta_destino_id, trans.tipo_cuenta_destino)}`
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={trans.tipo === 'INGRESO' ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(trans.monto)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Transacciones;
